import { ChevronRightIcon } from '@radix-ui/react-icons';
import { Box, Card, Flex, PageNav, Separator, Text } from '@workos-inc/design-system';
import { mdxPlugins, remarkScreenshots } from '@workos-inc/docs-kit';
import fs from 'fs';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import NextLink from 'next/link';
import { MDXRemote, MDXRemoteProps } from 'next-mdx-remote';
import { serialize } from 'next-mdx-remote/serialize';
import path from 'path';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { CopyMarkdownButton } from 'src/components/copy-markdown-button';
import { Header } from 'src/components/header';
import { mdxComponentsBySlug } from 'src/components/mdx-components';
import { NavigationWrapper } from 'src/components/navigation-wrapper';
import { PageBackground } from 'src/components/page-background';
import { PageLayout } from 'src/components/page-layout';
import { ReferenceDataProvider, ReferenceDescriptionsProvider } from 'src/components/reference-data-provider';
import { CONTENT_DIR } from 'src/utils/constants';
import { createPageImage, createPageTitle } from 'src/utils/create-page-metadata';
import { NavMeta, NavMetaLink } from 'src/utils/nav-meta';
import { findDeepestIndexSlug, getSiblingMdxFiles } from 'src/utils/reference-files';
import { getReferenceMeta, ReferenceMeta } from 'src/utils/reference-meta';
import { buildMergedPageRedirects, getNavigationUrlsForSubFeature, getNextPageInfo, getReferenceNavMeta } from 'src/utils/reference-nav';
import { filePathToReferenceSlug, findReferenceSubFeaturePages } from 'src/utils/reference-pages';
import { rehypeCodeBlockHoverCards } from 'src/utils/rehype-code-block-hover-cards';
import { rehypeCodeBlockReplacements } from 'src/utils/rehype-code-block-replacements';
import { rehypeCodeBlockSnippets } from 'src/utils/rehype-code-block-snippets';
import { rehypeReferenceNav } from 'src/utils/rehype-reference-nav';
import { FeatureFlag, useIsFeatureFlagEnabled } from 'src/utils/use-feature-flags';
import { usePathname } from 'src/utils/use-pathname';
interface ReferenceSection {
  code: string;
  slug: string;
  featureFlag?: FeatureFlag;
  externalAssets?: Record<string, string>;
}
interface NextPageMeta {
  title: string;
  description: string;
}
export const getStaticPaths: GetStaticPaths = async () => {
  const subFeaturePaths = await findReferenceSubFeaturePages();
  const paths: {
    params: {
      slug: string[];
    };
  }[] = [];

  // Add paths for sub-feature pages (e.g., /reference/authkit/user)
  for (const slug of subFeaturePaths) {
    paths.push({
      params: {
        slug
      }
    });

    // For each sub-feature, also add paths for individual endpoint URLs
    // (e.g., /reference/authkit/user/get)
    const referenceDir = path.join(process.cwd(), CONTENT_DIR, 'reference');
    const subFeatureDir = path.join(referenceDir, ...slug);
    if (fs.existsSync(subFeatureDir)) {
      const files = await fs.promises.readdir(subFeatureDir);
      const endpointFiles = files.filter(file => file.endsWith('.mdx') && file !== 'index.mdx');
      for (const endpointFile of endpointFiles) {
        const endpointName = endpointFile.replace('.mdx', '');
        paths.push({
          params: {
            slug: [...slug, endpointName]
          }
        });
      }
    }
  }
  return {
    paths,
    fallback: 'blocking'
  };
};
export const getStaticProps: GetStaticProps<ReferencePageProps> = async context => {
  if (context.params === undefined) {
    return {
      notFound: true
    };
  }
  const slug = context.params.slug ? [context.params.slug].flat() : [];
  const referenceMeta = await getReferenceMeta({
    cached: false
  });
  const navMeta = getReferenceNavMeta();

  // For nested paths (e.g., /reference/sso/profile/get-user-profile),
  // load from the root section directory and use nested path as anchor

  let subFeatureSlug = slug;
  if (slug.length >= 1) {
    // Find the deepest path that has an index.mdx file
    // This makes each folder with index.mdx its own page
    subFeatureSlug = findDeepestIndexSlug(slug);
  }
  const mdxFiles = await getSiblingMdxFiles(subFeatureSlug);
  if (mdxFiles.length === 0) {
    return {
      notFound: true
    };
  }

  // Sort files according to navigation order
  // First, get all URLs from navigation for this sub-feature
  const navigationUrls = getNavigationUrlsForSubFeature(subFeatureSlug, navMeta.links);
  const urlToOrder = new Map<string, number>();
  navigationUrls.forEach((url, index) => {
    urlToOrder.set(url, index);

    // Map hash URLs to path equivalents so file slug lookups work
    // e.g. /reference#client-libraries -> /reference/client-libraries
    const hashIndex = url.indexOf('#');
    if (hashIndex !== -1) {
      const basePath = url.substring(0, hashIndex);
      const fragment = url.substring(hashIndex + 1);
      urlToOrder.set(`${basePath}/${fragment}`, index);
    }
  });

  // Sort files according to navigation order
  mdxFiles.sort((a, b) => {
    const aUrl = filePathToReferenceSlug(a);
    const bUrl = filePathToReferenceSlug(b);
    const aOrder = urlToOrder.get(aUrl);
    const bOrder = urlToOrder.get(bUrl);

    // If both are in navigation, sort by order
    if (aOrder !== undefined && bOrder !== undefined) {
      return aOrder - bOrder;
    }

    // If only one is in navigation, prioritize it (navigation items come first)
    if (aOrder !== undefined) {
      return -1;
    }
    if (bOrder !== undefined) {
      return 1;
    }

    // If neither is in navigation, maintain original order (don't sort)
    return 0;
  });
  const sections: ReferenceSection[] = [];
  for (const filePath of mdxFiles) {
    // Convert file path to reference slug
    const referenceSlugPath = filePathToReferenceSlug(filePath);
    const rawMdx = await new Promise<Buffer>(resolve => fs.readFile(filePath, (_, data) => resolve(data)));
    const {
      compiledSource: code,
      frontmatter
    } = await serialize(rawMdx, {
      parseFrontmatter: true,
      mdxOptions: {
        remarkPlugins: [...mdxPlugins.remark, remarkScreenshots],
        rehypePlugins: [rehypeCodeBlockSnippets(filePath), ...mdxPlugins.rehype, rehypeCodeBlockHoverCards(referenceMeta.reference), rehypeCodeBlockReplacements, rehypeReferenceNav()]
      }
    });
    const sectionData: ReferenceSection = {
      code,
      slug: referenceSlugPath
    };
    if (frontmatter.featureFlag) {
      // Introducing lint rule banning type assertions
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      sectionData.featureFlag = frontmatter.featureFlag as FeatureFlag;
    }
    if (frontmatter.externalAssets) {
      const externalAssets: Record<string, string> = {};
      const assets = frontmatter.externalAssets;
      for (const [key, url] of Object.entries(assets)) {
        const response = await fetch(url);
        if (url.endsWith('json')) {
          externalAssets[key] = await response.json();
        } else {
          externalAssets[key] = await response.text();
        }
      }
      sectionData.externalAssets = externalAssets;
    }
    sections.push(sectionData);
  }

  // Get page metadata from the index.mdx file if it exists
  const indexFile = mdxFiles.find(file => file.endsWith('index.mdx'));
  let title = 'API Reference';
  let description = 'Code snippets and type definitions for the WorkOS client libraries.';
  let hasReferenceFrontmatter = false;
  if (indexFile) {
    const indexRawMdx = await new Promise<Buffer>(resolve => fs.readFile(indexFile, (_, data) => resolve(data)));
    const {
      frontmatter: indexFrontmatter
    } = await serialize(indexRawMdx, {
      parseFrontmatter: true
    });
    if (indexFrontmatter.title && typeof indexFrontmatter.title === 'string') {
      title = indexFrontmatter.title;
    }
    if (indexFrontmatter.description && typeof indexFrontmatter.description === 'string') {
      description = indexFrontmatter.description;
    }

    // Check if this page has reference frontmatter (language-specific API docs)
    if (indexFrontmatter.reference) {
      hasReferenceFrontmatter = true;
    }
  }

  // Use the sub-feature slug for canonical URL (not the endpoint URL)
  const currentPageUrl = subFeatureSlug.length === 0 ? '/reference' : `/reference/${subFeatureSlug.join('/')}`;
  const canonicalUrl = `https://workos.com/docs${currentPageUrl}`;
  const image = await createPageImage(title, description);

  // Get next page metadata for "Up next" card
  let nextPageMeta: NextPageMeta | null = null;
  let nextPageUrl: string | null = null;
  const nextPageInfo = getNextPageInfo(currentPageUrl, navMeta.links);
  if (nextPageInfo) {
    nextPageUrl = nextPageInfo.url;
    const nextSlugParts = nextPageInfo.url.replace('/reference/', '').split('/').filter(Boolean);
    const nextIndexPath = path.join(process.cwd(), CONTENT_DIR, 'reference', ...nextSlugParts, 'index.mdx');

    // Try to get description from frontmatter, fall back to a default
    let nextDescription = 'Continue to the next section';
    if (fs.existsSync(nextIndexPath)) {
      const nextRawMdx = await new Promise<Buffer>(resolve => fs.readFile(nextIndexPath, (_, data) => resolve(data)));
      const {
        frontmatter: nextFrontmatter
      } = await serialize(nextRawMdx, {
        parseFrontmatter: true
      });
      if (nextFrontmatter.description && typeof nextFrontmatter.description === 'string') {
        nextDescription = nextFrontmatter.description;
      }
    }
    nextPageMeta = {
      title: nextPageInfo.title,
      description: nextDescription
    };
  }
  const mergedPageRedirects = buildMergedPageRedirects(currentPageUrl, navMeta.links);
  return {
    props: {
      sections: [sections],
      navMeta,
      referenceMeta,
      pageMeta: {
        title,
        canonicalUrl,
        description,
        image
      },
      nextPageMeta,
      nextPageUrl,
      mergedPageRedirects,
      hasReferenceFrontmatter
    }
  };
};
interface ReferencePageProps {
  sections: ReferenceSection[][];
  navMeta: NavMeta;
  referenceMeta: ReferenceMeta;
  pageMeta: {
    title: string;
    canonicalUrl: string;
    description: string;
    image: string;
  };
  nextPageMeta: NextPageMeta | null;
  nextPageUrl: string | null;
  mergedPageRedirects: Record<string, string>;
  hasReferenceFrontmatter: boolean;
}
const ReferencePage = ({
  navMeta,
  sections,
  referenceMeta,
  pageMeta,
  nextPageMeta,
  nextPageUrl,
  mergedPageRedirects,
  hasReferenceFrontmatter
}: ReferencePageProps) => {
  const title = createPageTitle(pageMeta.title);
  const {
    description,
    image
  } = pageMeta;
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [copyButtonTarget, setCopyButtonTarget] = React.useState<HTMLElement | null>(null);
  const pathname = usePathname();
  const pageSlug = React.useMemo(() => pathname?.split('/').filter(Boolean) ?? [], [pathname]);
  React.useEffect(() => {
    const container = contentRef.current;
    if (!container) {
      return;
    }
    const firstH1 = container.querySelector('h1');
    if (!firstH1) {
      return;
    }

    // Insert after the PermanentLink wrapper (if present) rather than after
    // the h1 itself, so the anchor link icon stays vertically centered on the
    // heading instead of shifting down to account for the portal content.
    const permanentLink = firstH1.closest('.PermanentLink');
    const insertAfter = permanentLink ?? firstH1;
    const target = document.createElement('div');
    insertAfter.after(target);
    setCopyButtonTarget(target);
    return () => {
      target.remove();
      setCopyButtonTarget(null);
    };
  }, [pageSlug]);
  return <Box data-sentry-element="Box" data-sentry-component="ReferencePage" data-sentry-source-file="[[...slug]].tsx">
      <Head data-sentry-element="Head" data-sentry-source-file="[[...slug]].tsx">
        <title>{title}</title>
        <meta content={description} name="description" data-sentry-element="meta" data-sentry-source-file="[[...slug]].tsx" />
        <meta content={image} itemProp="image" data-sentry-element="meta" data-sentry-source-file="[[...slug]].tsx" />
        <meta content={image} name="image" data-sentry-element="meta" data-sentry-source-file="[[...slug]].tsx" />
        <meta content={description} name="og:description" data-sentry-element="meta" data-sentry-source-file="[[...slug]].tsx" />
        <meta content={image} property="og:image" data-sentry-element="meta" data-sentry-source-file="[[...slug]].tsx" />
        <meta content={title} property="og:title" data-sentry-element="meta" data-sentry-source-file="[[...slug]].tsx" />
        <meta content="website" property="og:type" data-sentry-element="meta" data-sentry-source-file="[[...slug]].tsx" />
        <meta content="summary_large_image" name="twitter:card" data-sentry-element="meta" data-sentry-source-file="[[...slug]].tsx" />
        <meta content={description} name="twitter:description" data-sentry-element="meta" data-sentry-source-file="[[...slug]].tsx" />
        <meta content={image} name="twitter:image" data-sentry-element="meta" data-sentry-source-file="[[...slug]].tsx" />
        <meta content={title} name="twitter:title" data-sentry-element="meta" data-sentry-source-file="[[...slug]].tsx" />
        <link href={pageMeta.canonicalUrl} rel="canonical" />
      </Head>

      <PageBackground data-sentry-element="PageBackground" data-sentry-source-file="[[...slug]].tsx" />

      <Header caption={navMeta.title} data-sentry-element="Header" data-sentry-source-file="[[...slug]].tsx" />

      <PageLayout.Root layout="sidebar" data-sentry-element="PageLayout.Root" data-sentry-source-file="[[...slug]].tsx">
        <PageLayout.Nav data-sentry-element="PageLayout.Nav" data-sentry-source-file="[[...slug]].tsx">
          <NavigationWrapper data-sentry-element="NavigationWrapper" data-sentry-source-file="[[...slug]].tsx">
            <PageNav.Root color="purple" data-sentry-element="PageNav.Root" data-sentry-source-file="[[...slug]].tsx">
              {navMeta.links.map(link => <ReferenceNavRecursive key={link.title} {...link} mergedPageRedirects={mergedPageRedirects} />)}
            </PageNav.Root>
          </NavigationWrapper>
        </PageLayout.Nav>

        <PageLayout.Content data-algolia-exclude={hasReferenceFrontmatter ? true : undefined} data-sentry-element="PageLayout.Content" data-sentry-source-file="[[...slug]].tsx">
          <div ref={contentRef}>
            <Box pb={{
            initial: '8',
            sm: '9'
          }} data-sentry-element="Box" data-sentry-source-file="[[...slug]].tsx">
              <ReferenceDataProvider value={referenceMeta.reference} data-sentry-element="ReferenceDataProvider" data-sentry-source-file="[[...slug]].tsx">
                <ReferenceDescriptionsProvider value={referenceMeta.descriptions} data-sentry-element="ReferenceDescriptionsProvider" data-sentry-source-file="[[...slug]].tsx">
                  {sections.map((group, index) => <React.Fragment key={index}>
                      {group.map(props => <ReferenceSection key={props.slug} {...props} />)}

                      {index < sections.length - 1 && <Separator mb={{
                    initial: '7',
                    sm: '8',
                    md: '9'
                  }} size="4" />}
                    </React.Fragment>)}
                </ReferenceDescriptionsProvider>
              </ReferenceDataProvider>

              {nextPageMeta && nextPageUrl && <Card asChild my="6" size={{
              initial: '2',
              xs: '3'
            }} variant="classic">
                  <NextLink href={nextPageUrl}>
                    <Flex align="stretch" gap={{
                  initial: '3',
                  xs: '5'
                }} justify="between">
                      <Flex direction="column">
                        <Text size="3" trim="start" weight="bold">
                          {nextPageMeta.title}
                        </Text>
                        <Text color="gray" size="2" trim="end">
                          {nextPageMeta.description.replace(/\.$/, '')}
                        </Text>
                      </Flex>

                      <Flex align="center" display={{
                    xs: 'none'
                  }} mx="-1">
                        <ChevronRightIcon color="var(--gray-a9)" height="20" width="20" />
                      </Flex>

                      <Flex align="center" display={{
                    initial: 'none',
                    xs: 'flex'
                  }} flexShrink="0" gap="5">
                        <Separator orientation="vertical" size="2" />
                        <Flex align="center" gap="1">
                          <Text as="div" color="gray" size="2" style={{
                        marginTop: -2
                      }}>
                            Up next
                          </Text>
                          <ChevronRightIcon color="var(--gray-a9)" height="16" width="16" />
                        </Flex>
                      </Flex>
                    </Flex>
                  </NextLink>
                </Card>}
            </Box>
          </div>
          {copyButtonTarget && createPortal(<CopyMarkdownButton slug={pageSlug} />, copyButtonTarget)}
        </PageLayout.Content>
      </PageLayout.Root>
    </Box>;
};
const ReferenceSection = ({
  code,
  slug,
  featureFlag,
  externalAssets
}: ReferenceSection) => {
  const isAllowed = useIsFeatureFlagEnabled(featureFlag);
  if (!isAllowed) {
    return null;
  }
  return <Box data-section-slug={slug} mb={{
    initial: '7',
    sm: '8',
    md: '9'
  }} tabIndex={-1} data-sentry-element="Box" data-sentry-component="ReferenceSection" data-sentry-source-file="[[...slug]].tsx">
      <MDXRemote compiledSource={code} frontmatter={mdxRemoteFrontmatter} scope={externalAssets ? {
      externalAssets
    } : mdxRemoteScope} components={
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    mdxComponentsBySlug.reference as MDXRemoteProps['components']} data-sentry-element="MDXRemote" data-sentry-source-file="[[...slug]].tsx" />
    </Box>;
};

// Don't pass empty object to MDX Remote directly.
// MDX Remote must receive a stable object, otherwise performance on large pages suffers.
const mdxRemoteFrontmatter = {};
const mdxRemoteScope = {};
const ReferenceNavRecursive = ({
  featureFlag,
  links,
  title,
  url,
  mergedPageRedirects
}: NavMetaLink & {
  mergedPageRedirects: Record<string, string>;
}) => {
  const sectionInView = usePathname();
  const state = sectionInView === url ? 'active' : 'normal';
  const isAllowed = useIsFeatureFlagEnabled(featureFlag);
  if (!isAllowed) {
    return null;
  }
  if (url === undefined) {
    return <>
        <PageNav.Label>{title}</PageNav.Label>
        {links?.map(link => <ReferenceNavRecursive key={link.title} {...link} mergedPageRedirects={mergedPageRedirects} />)}
      </>;
  }

  // URLs from navigation already include /reference and should be absolute paths
  // Next.js will handle basePath (/docs) automatically
  const href = url.startsWith('/') ? url : `/${url}`;
  if (links) {
    const subOpen = state === 'active' || sectionInView?.startsWith(url + '/');
    return <PageNav.Sub key={url} open={subOpen}>
        <PageNav.Link asChild state={state}>
          <NextLink href={href}>{title}</NextLink>
        </PageNav.Link>
        <PageNav.SubContent>
          {links.map(link => {
          // Use pre-computed hash URL if this link is merged into the parent page
          const childUrl = link.url;
          const redirectUrl = childUrl ? mergedPageRedirects[childUrl] : undefined;
          return <ReferenceNavRecursive key={link.title} {...link} mergedPageRedirects={mergedPageRedirects} url={redirectUrl ?? link.url} />;
        })}
        </PageNav.SubContent>
      </PageNav.Sub>;
  }
  return <PageNav.Link asChild state={state} data-sentry-element="PageNav.Link" data-sentry-component="ReferenceNavRecursive" data-sentry-source-file="[[...slug]].tsx">
      <NextLink href={href} data-sentry-element="NextLink" data-sentry-source-file="[[...slug]].tsx">{title}</NextLink>
    </PageNav.Link>;
};
export default ReferencePage;